/**
 * Route helpers for check pages. Check numbers are free text — Super Coffee keys ACH
 * remittances as `ACH# 09-01-2026` — so a number dropped raw into the URL loses everything
 * after the `#` to the fragment. Every navigation encodes and every route param decodes
 * through these helpers (wouter-preact 2.x hands back the raw, still-encoded segment).
 *
 * Plain percent-encoding (`ACH%23%2009-01-2026`) is not enough: the production `/api` rewrite
 * (Render) decodes the path before forwarding it, so the server receives a raw `#` and the route
 * truncated to `/accounting/check/ACH` — locally vite forwards the bytes untouched, which is why
 * it only failed in prod. `~` is unreserved (RFC 3986) and no proxy touches it, so every `%XX`
 * escape is written as `~XX` instead (`ACH~23~2009-01-2026`). The server reverses it in
 * `decode_check_path_segment`. Literal `~` is escaped first (`~7E`) so the mapping stays
 * one-to-one; no production check number contains one.
 */

export function encode_check_segment(number: string): string {
  return encodeURIComponent(number).replace(/~/g, "%7E").replace(/%/g, "~")
}

export function check_detail_path(number: string): string {
  return `/accounting/check/${encode_check_segment(number)}`
}

export function check_apply_path(number: string, accounting_check_id?: string): string {
  const suffix = accounting_check_id ? `?accountingCheckId=${encodeURIComponent(accounting_check_id)}` : ""
  return `${check_detail_path(number)}/apply${suffix}`
}

/** API path for the check detail endpoint, from a check number. Append `/backup-zip` etc. */
export function check_api_path(number: string): string {
  return `/accounting/check/${encode_check_segment(number)}`
}

/**
 * Decode the `:checkNumber` route segment back into the check's actual number. Accepts the
 * tilde form and, for links minted before it, a plain percent-encoded or raw segment.
 */
export function decode_check_number(param: string): string {
  const escaped = param.includes("~") ? param.replace(/~/g, "%") : param
  try {
    return decodeURIComponent(escaped)
  } catch {
    return param
  }
}

if (import.meta.vitest) {
  const { describe, it, expect } = import.meta.vitest

  describe("check_path", () => {
    it("writes escapes with a tilde so a proxy that decodes the path cannot see a #", () => {
      expect(check_detail_path("ACH# 09-01-2026")).toBe("/accounting/check/ACH~23~2009-01-2026")
      expect(check_detail_path("ACH# 09-01-2026")).not.toContain("%")
      expect(check_apply_path("ACH# 09-01-2026", "abc")).toBe(
        "/accounting/check/ACH~23~2009-01-2026/apply?accountingCheckId=abc"
      )
    })

    it("round-trips through the route param, trailing space included", () => {
      for (const number of ["ACH# 09-01-2026 ", "4345810", "12% off?", "a~b", "EAST-368813"]) {
        const segment = check_detail_path(number).split("/").pop()!
        expect(decode_check_number(segment)).toBe(number)
      }
    })

    it("still decodes links minted with plain percent-encoding", () => {
      expect(decode_check_number("ACH%23%2009-01-2026%20")).toBe("ACH# 09-01-2026 ")
    })

    it("leaves plain numbers and malformed params alone", () => {
      expect(check_detail_path("4345810")).toBe("/accounting/check/4345810")
      expect(decode_check_number("100%")).toBe("100%")
    })
  })
}
